import React, { useState, useEffect, useRef } from 'react';

// Pill in the top bar that shows/hides the server-rendered "About this circuit"
// panel (#circuit-about, experiments/_about). Nothing is fetched or rendered
// here: the panel is already in the page HTML with the `hidden` attribute.
const PANEL_ID = 'circuit-about';
const SLIDE_MS = 200;

function AboutPill({ label, compact }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const frame = useRef(null);

  // Apply the state to the panel: unhide, then slide in; slide out, then hide.
  useEffect(() => {
    const panel = document.getElementById(PANEL_ID);
    if (!panel) return undefined;
    clearTimeout(timer.current);
    document.body.classList.toggle('circuit-about-open', open);
    if (open) {
      const top = document.getElementById('top-bar') || document.getElementById('top-bar-mobile');
      if (top) panel.style.top = top.getBoundingClientRect().bottom + 'px';
      panel.hidden = false;
      frame.current = requestAnimationFrame(() => {
        frame.current = requestAnimationFrame(() => panel.classList.add('is-open'));
      });
    } else {
      panel.classList.remove('is-open');
      timer.current = setTimeout(() => { panel.hidden = true; }, SLIDE_MS);
    }
    return () => {
      clearTimeout(timer.current);
      cancelAnimationFrame(frame.current);
      document.body.classList.remove('circuit-about-open');
    };
  }, [open]);

  // The close buttons live in the server-rendered panel; Escape closes too.
  useEffect(() => {
    const onClick = (event) => {
      if (event.target.closest && event.target.closest('[data-about-close]')) {
        event.preventDefault();
        setOpen(false);
      }
    };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <button
      type="button"
      className={`about-pill${compact ? ' about-pill--compact' : ''}${open ? ' is-open' : ''}`}
      aria-label={compact ? (label || 'About') : undefined}
      aria-controls={PANEL_ID}
      aria-expanded={open}
      onClick={() => setOpen(!open)}
    >
      <span className="about-pill__icon" aria-hidden="true">i</span>
      {!compact && (label || 'About')}
    </button>
  );
}

export default AboutPill;
